import { useApolloClient } from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import {
  evictPricesByProductType,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidatePricesByProductType } from "shared/api/price/modifyPriceCache"
import { useProjectPriceLazyQuery } from "shared/api/price/queries.generated"
import { DeleteResponseTypename, MutationTypename } from "shared/api/types"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { DELETE_SPECIAL_PRICE_GROUP } from "shared/types/ProjectMutations"

import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import {
  DeleteSpecialPriceGroupMutation,
  DeleteSpecialPriceGroupMutationVariables,
  useDeleteSpecialPriceGroupMutation
} from "entities/projects/graphql/special-price-groups/mutations.generated"

import { evictSpecialPriceGroup } from "./evictSpecialPriceGroup"

export function useDeleteSpecialPriceGroup() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()

  const [mutate] = useDeleteSpecialPriceGroupMutation()

  const [fetchSpecialPriceGroups] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [fetchProjectPrice] = useProjectPriceLazyQuery()
  const [fetchProjects] = useProjectsLazyQuery()
  const startProjectRequest = useProjectRequestMonitor()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.SpecialPriceGroup.DELETE_SPECIAL_PRICE_GROUP.Start"),
      success: t(
        "Request.SpecialPriceGroup.DELETE_SPECIAL_PRICE_GROUP.Success"
      ),
      failure: t("Request.SpecialPriceGroup.DELETE_SPECIAL_PRICE_GROUP.Failure")
    }
  })

  return useEvent(
    async ({
      projectID,
      productType
    }: {
      productType: string
      projectID: string
    }) => {
      const specialPriceGroups = await fetchSpecialPriceGroups({
        variables: { id: projectID }
      })
      const specialPriceGroup =
        specialPriceGroups.data?.project?.specialPriceGroups?.find(
          group => group.productType === productType
        )

      if (!specialPriceGroup) {
        return undefined
      }

      evictSpecialPriceGroup(specialPriceGroup.id, apolloCache)
      invalidatePricesByProductType({
        projectID,
        cache: apolloCache,
        productType: specialPriceGroup.productType
      })

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DELETE_SPECIAL_PRICE_GROUP
      })

      return callMutation<
        DeleteSpecialPriceGroupMutation,
        DeleteSpecialPriceGroupMutationVariables,
        boolean
      >(
        options =>
          mutate(options).then(result => {
            if (result.data?.specialPriceGroup?.success) {
              evictSpecialPriceGroup(specialPriceGroup.id, apolloCache)
            }
            return result
          }),
        {
          variables: { id: specialPriceGroup.id, projectID },
          transform: data => data.specialPriceGroup.success,
          apolloOptions: {
            optimisticResponse: {
              __typename: MutationTypename,
              specialPriceGroup: {
                __typename: DeleteResponseTypename,
                success: true
              }
            },
            onCompleted: () => {
              evictPricesByProductType({
                projectID,
                productType: specialPriceGroup.productType,
                cache: apolloCache
              })
              evictSpecialPriceGroup(specialPriceGroup.id, apolloCache)
              completeProjectRequest()
              fetchSpecialPriceGroups({ variables: { id: projectID } })
              evictProjectHistory(projectID, apolloCache)
              raiseSuccessToast()
            },
            onError: () => {
              completeProjectRequest()
              fetchProjectPrice({
                variables: { id: projectID },
                fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
              })
              fetchSpecialPriceGroups({ variables: { id: projectID } })
              evictProjectHistory(projectID, apolloCache)
              evictProjectPrice(projectID, apolloCache, true)
              fetchProjects()
              raiseErrorToast()
            }
          }
        }
      )
    }
  )
}
